import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkle, Translate } from "@phosphor-icons/react";

// Top-10 languages. ISO-639-1 codes match the backend validator.
const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español (Spanish)" },
  { code: "fr", label: "Français (French)" },
  { code: "de", label: "Deutsch (German)" },
  { code: "pt", label: "Português (Portuguese)" },
  { code: "it", label: "Italiano (Italian)" },
  { code: "nl", label: "Nederlands (Dutch)" },
  { code: "ar", label: "العربية (Arabic)" },
  { code: "zh", label: "中文 (Chinese)" },
  { code: "ja", label: "日本語 (Japanese)" },
];
const SUPPORTED = new Set(LANGUAGES.map((l) => l.code));

function detectBrowserLanguage() {
  try {
    const raw = (navigator.language || "en").toLowerCase();
    const base = raw.split("-")[0];
    return SUPPORTED.has(base) ? base : "en";
  } catch {
    return "en";
  }
}

export default function GenerateProposalSection({
  user, topic, setTopic, onGenerate, generating, errMessage,
  language, setLanguage,
}) {
  const browserDefault = useMemo(detectBrowserLanguage, []);
  const effective = language || browserDefault;

  return (
    <div className="mt-8 border border-zinc-200 bg-white p-6 md:p-8" data-testid="generate-proposal-section">
      <div className="gf-overline">Draft a proposal with AI</div>
      <h2 className="font-display font-bold text-2xl mt-2 tracking-tight">
        What's your project about?
      </h2>
      <p className="text-sm text-zinc-600 mt-2">
        Describe your project or initiative in a few sentences. Claude Sonnet 4.5 will draft
        a complete 9-section proposal tailored to this grant, for <b>{user?.name}</b>
        {user?.organization ? ` at ${user.organization}` : ""}.
      </p>
      <Label className="gf-overline mt-5 block">Project Topic / Intent</Label>
      <Textarea
        data-testid="topic-input"
        value={topic}
        onChange={(e) => setTopic(e.target.value)}
        placeholder="e.g., Deploying a community broadband network across 14 rural counties in North Dakota, targeting underserved tribal areas with subsidized last-mile fiber."
        rows={5}
        className="gf-sharp mt-2 border-zinc-300 bg-white"
      />

      <div className="mt-5 flex items-end gap-3 flex-wrap" data-testid="language-row">
        <div>
          <Label className="gf-overline block flex items-center gap-1.5">
            <Translate size={12} weight="bold" /> Output language
          </Label>
          <Select value={effective} onValueChange={(v) => setLanguage?.(v)}>
            <SelectTrigger
              data-testid="language-select"
              className="gf-sharp mt-2 w-64 border-zinc-300 bg-white"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((l) => (
                <SelectItem key={l.code} value={l.code} data-testid={`lang-opt-${l.code}`}>
                  {l.label}
                  {l.code === browserDefault && (
                    <span className="ml-2 text-[10px] text-zinc-500 font-mono">browser</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-zinc-500 font-mono pb-2">
          Detected from your browser: <b>{browserDefault.toUpperCase()}</b>
        </div>
      </div>

      {errMessage && (
        <div data-testid="generate-error" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 p-3">
          {String(errMessage)}
        </div>
      )}
      <Button
        onClick={onGenerate}
        disabled={generating}
        data-testid="generate-btn"
        className="gf-sharp mt-5 bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide px-6"
      >
        <Sparkle size={18} className="mr-2" weight="fill" />
        {generating ? "Drafting proposal… (up to 60s)" : "Generate draft"}
      </Button>
    </div>
  );
}
